/**
 * OAuth Callback Page
 *
 * Handles the callback from WayFong Auth service after Google OAuth flow.
 * Extracts the access token from the URL fragment, validates it, and
 * redirects to the intended destination.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOAuth } from '../hooks/useOAuth';
import { extractCodeFromQuery, exchangeCodeForToken, getRedirectUrl } from '../services/oauthService';
import { useLanguage } from '../i18n/LanguageContext';
import { AlertCircle, RefreshCw } from 'lucide-react';

const OAuthCallback: React.FC = () => {
  const navigate = useNavigate();
  const { validateAndStoreToken, clearOAuthAndRestart } = useOAuth();
  const { t } = useLanguage();
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(true);

  useEffect(() => {
    const processCallback = async () => {
      // Extract authorization code from URL query params (?code=xxx)
      const code = extractCodeFromQuery();

      if (!code) {
        // Check for error in URL query params
        const params = new URLSearchParams(window.location.search);
        const errorParam = params.get('error');
        const errorDesc = params.get('error_description');

        if (errorParam) {
          setError(errorDesc || errorParam);
        } else {
          setError('No authorization code received');
        }
        setIsProcessing(false);
        return;
      }

      // Exchange the authorization code for an access token
      const exchanged = await exchangeCodeForToken(code);

      if (!exchanged) {
        setError('Failed to exchange authorization code for access token');
        setIsProcessing(false);
        return;
      }

      // Validate the token with WayFong Auth service and store user info.
      // Forward expiresIn so the scheduler knows when to re-check.
      const isValid = await validateAndStoreToken(exchanged.accessToken, exchanged.expiresIn);

      if (isValid) {
        // Get the intended redirect URL (or default to root)
        const redirectUrl = getRedirectUrl();

        // Clean the URL — remove ?code= query param
        window.history.replaceState(null, '', window.location.pathname);

        // Navigate to intended destination
        navigate(redirectUrl, { replace: true });
      } else {
        setError('Token validation failed');
        setIsProcessing(false);
      }
    };

    processCallback();
  }, [navigate, validateAndStoreToken]);

  // Processing state
  if (isProcessing) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-secondary-50 dark:bg-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-secondary-600 dark:text-secondary-400">
            {t('oauth.validating')}
          </p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-secondary-50 dark:bg-gray-900 px-4">
        <div className="max-w-md w-full text-center">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8">
            <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-secondary-900 dark:text-white mb-2">
              {t('oauth.error')}
            </h2>
            <p className="text-secondary-600 dark:text-secondary-400 mb-6">
              {error}
            </p>
            <button
              onClick={clearOAuthAndRestart}
              className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors duration-200"
            >
              <RefreshCw className="h-5 w-5 mr-2" />
              {t('oauth.retry')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default OAuthCallback;
